import { React, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import './visual.css';
import '../components.css';

function ScrollToTopOnMount() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return null;
}

export function VisualExplanations() {
    return (
        <section id="veac" className="components">
            <ScrollToTopOnMount />

            <div className="return">
                <Link to="/">[ Return ]</Link>
            </div>

            <Container fluid className="logos" style={{ marginTop: '60px' }}>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}>
                        <img className="logo" src="images/logos/dbvis-logo.png" alt="DBVIS Logo" />
                    </Col>
                    <Col></Col>
                    <Col sm={2}>
                        <img className="logo" src="images/logos/unikn-logo.png" alt="UNI KN Logo" />
                        <img className="logo" src="images/logos/hsof-logo.png" alt="HS Offenburg Logo" />
                    </Col>
                </Row>
            </Container>
            <Container fluid>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h1>
                            Visual Explanations with Attributions and Counterfactuals<br></br> on
                            Time Series Classification
                        </h1>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}></Col>
                    <Col>
                        <h3>
                            Udo Schlegel<sup>1</sup>, Daniela Oelke<sup>2</sup>, Daniel A. Keim
                            <sup>1</sup>, Mennatallah El-Assady<sup>3</sup>
                        </h3>
                        <h3>
                            <sup>1</sup> University of Konstanz <span className="text-divider"></span>
                            <sup>2</sup> Offenburg University of Applied Sciences{' '}
                            <span className="text-divider"></span>
                            <sup>3</sup> ETH AI Center, ETH Zurich
                        </h3>
                    </Col>
                    <Col sm={2}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col className="text-align-center" sm={10}>
                        <a href="https://arxiv.org/abs/2307.08494">
                            [ arXiv ]
                        </a>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}></Col>
                    <Col className="text-align-center">
                        <a href="https://arxiv.org/pdf/2307.08494">[ Paper ]</a>
                    </Col>
                    <Col className="text-align-center">
                        <a href="https://arxiv.org/abs/2307.08494">[ arXiv ]</a>
                    </Col>
                    <Col className="text-align-center">
                        <a href="https://visual-explanations.time-series-xai.dbvis.de">[ Demo ]</a>
                    </Col>
                    <Col className="text-align-center">
                        <a href="https://github.com/visual-xai-for-time-series/visual-explanations-on-time-series">
                            [ Code ]
                        </a>
                    </Col>
                    <Col sm={2}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            Explainable AI (XAI) methods operate on{' '}
                            <span className="bold">varying abstraction levels</span>: a{' '}
                            <span className="bold">global level</span> explains the overall behavior
                            of a model, while a <span className="bold">local level</span> explains
                            single sample predictions. In most cases, only one of the two is offered,
                            and switching between them is tedious. We propose a{' '}
                            <span className="bold">visual analytics workflow</span> that supports
                            seamless transitions between global and local explanations, focusing on{' '}
                            <span className="bold">attributions</span> and{' '}
                            <span className="bold">counterfactuals</span> on time series
                            classification.
                        </p>
                        <p>
                            We adapt local attribution techniques originally developed for images and
                            text, e.g., LRP<sup>[1]</sup>, SHAP<sup>[2]</sup>, and LIME<sup>[3]</sup>
                            , to time series, a data type that is far{' '}
                            <span className="bold">less intelligible</span> to humans than images or
                            text. To create a global overview, we apply local attribution methods to
                            every sample of a dataset and{' '}
                            <span className="bold">project the resulting explanations into two
                            dimensions</span>, revealing model behavior, strategies, and decision
                            boundaries. A <span className="bold">what-if analysis</span> then
                            facilitates hypothesis generation and verification on both the global and
                            the local level.
                        </p>
                    </Col>
                    <Col>
                        <img
                            className="images-text"
                            src="images/2024-veac/veac-workflow.png"
                            alt="Visual analytics workflow for explanations with attributions and counterfactuals"
                        />
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>From Local Attributions to Global Explanations</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            Our workflow consists of an{' '}
                            <span className="bold">automatic phase</span> and an{' '}
                            <span className="bold">explanation phase</span>. In the automatic phase,
                            a <span className="bold">perturbation analysis</span> first generates
                            attributions with several XAI techniques and pre-ranks them by how well
                            they explain the model. Then, <span className="bold">
                            transformation projections</span> reduce the raw time series, common
                            transformations (e.g., Fourier, SAX, DCT), model activations, and the
                            ranked attributions to two dimensions using techniques such as PCA and
                            UMAP<sup>[4]</sup>, similar to StrategyAtlas<sup>[5]</sup>.
                        </p>
                        <p>
                            The explanation phase then lets analysts loop between{' '}
                            <span className="bold">global exploration</span> of these projections and{' '}
                            <span className="bold">local inspection</span> of single samples.
                            Selecting or brushing interesting points, e.g., near a suspected decision
                            border or a misclassification, in the global view adds the corresponding
                            time series to the local what-if view for a detailed analysis, and
                            modified or generated samples can be projected back into the global view
                            to verify or reject a hypothesis about the model's decision-making.
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>Global Projection View</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <img
                            className="images-text"
                            src="images/2024-veac/veac-global-summary.png"
                            alt="Global projection view showing raw data, transformations, model activations, and attributions"
                        />
                    </Col>
                    <Col>
                        <p>
                            The global projection view arranges the projected{' '}
                            <span className="bold">raw time series (A)</span>,{' '}
                            <span className="bold">transformations (B)</span>,{' '}
                            <span className="bold">model activations (C)</span>, and{' '}
                            <span className="bold">attributions (D)</span> as a matrix of scatter
                            plots, one row per projection technique. As dozens of cells can quickly
                            overwhelm an analyst, a{' '}
                            <span className="bold">cluster score</span>, combining the Davies-Bouldin
                            score with the distance between cluster centroids, hides or shrinks
                            poorly-separated projections and highlights the most informative ones.
                        </p>
                        <p>
                            Points can be colored by{' '}
                            <span className="bold">ground truth</span>,{' '}
                            <span className="bold">prediction</span>, or the{' '}
                            <span className="bold">confusion matrix</span> to make, e.g., false
                            positives easy to spot. Contour lines and dense pixel distribution plots
                            on the axes further emphasize cluster shape and density, while hovering
                            shows a sample's details and brushing sends the selected samples to the
                            local what-if view.
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>Local What-If Analysis</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            Samples brushed from the global view, or filtered through a{' '}
                            <span className="bold">confusion matrix selector</span>, appear in the
                            local what-if view as a line plot with a heatmap-style background
                            encoding the attribution of each time point. Analysts can{' '}
                            <span className="bold">drag and drop time points</span>, with automatic
                            smoothing around the changed region, or brush a subsequence and set it to
                            the class activation maximization, the global or local mean, its inverse,
                            or a moving/exponential smoothing, and immediately see the effect on the
                            model's prediction and its uncertainty.
                        </p>
                        <p>
                            When manual edits are too tedious, the view offers{' '}
                            <span className="bold">nearest neighbor search</span> and automatic{' '}
                            <span className="bold">counterfactual generation</span> following Delaney
                            et al.<sup>[6]</sup>, which guides the search with a native neighbor from
                            the dataset. Newly created or modified samples can be{' '}
                            <span className="bold">reprojected</span> into the global view to check
                            whether they indeed cross a hypothesized decision border.
                        </p>
                    </Col>
                    <Col>
                        <img
                            className="images-text"
                            src="images/2024-veac/veac-attributions-and-counterfactuals.png"
                            alt="Local what-if analysis with counterfactual generation and reprojection to the global view"
                        />
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>The Application</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <img
                            className="images-text"
                            src="images/2024-veac/veac-application.png"
                            alt="Application overview with session view, global projection view, and local what-if analysis"
                        />
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            Our open-source application instantiates the workflow with four views.
                            The <span className="bold">settings view</span> starts a new automatic
                            phase for a chosen model, data, XAI methods, and transformations. The{' '}
                            <span className="bold">session view</span> is the entry point after
                            loading the application and lists precomputed sessions, ranked by their
                            automatic evaluation score, to pick up an analysis. The{' '}
                            <span className="bold">global projection view</span> and{' '}
                            <span className="bold">local what-if view</span> then form the explanation
                            loop described above, letting analysts move seamlessly between overview
                            and detail.
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>Use Cases</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            We verify the workflow with an expert study, working closely with two
                            signal processing engineers, and three use cases on the FordA<sup>[7]</sup>{' '}
                            engine-noise anomaly detection dataset.
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}></Col>
                    <Col>
                        <p className="bold">
                            Explore data transformations and feature relevance:
                        </p>
                        <ul>
                            <li>
                                Starting from the raw time series, transformations, and model
                                activations, analysts progressively find better-separated clusters
                                and higher cluster scores.
                            </li>
                            <li>
                                Attribution projections split the predictions even further, revealing
                                which XAI technique and projection combination best represents the
                                model's decisions and highlighting interesting samples for the local
                                view.
                            </li>
                        </ul>
                    </Col>
                    <Col sm={2}></Col>
                </Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}></Col>
                    <Col>
                        <p className="bold">Finding decision boundaries:</p>
                        <ul>
                            <li>
                                Selecting a misclassified and a correctly predicted sample near an
                                overlap in the model-activation projection, analysts generate
                                counterfactuals for both and compare the changes needed to flip each
                                prediction.
                            </li>
                            <li>
                                Reprojecting the counterfactuals back into the global view confirms
                                that they cross the hypothesized decision border, with an even
                                larger separation visible in the attribution projections than in the
                                raw data.
                            </li>
                        </ul>
                    </Col>
                    <Col sm={2}></Col>
                </Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}></Col>
                    <Col>
                        <p className="bold">Reason for misclassifications:</p>
                        <ul>
                            <li>
                                Modifying single high-relevance time points is often not enough to
                                flip a prediction; a focused, smoothed change of a whole subsequence
                                is usually required instead.
                            </li>
                            <li>
                                Comparing a weaker and a better-fitted model on the same
                                misclassification shows that the two models rely on different
                                patterns, something only visible by inspecting the local what-if
                                changes and the automatically generated counterfactual side by side.
                            </li>
                        </ul>
                    </Col>
                    <Col sm={2}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center">
                    <Col sm={1}></Col>
                    <Col>
                        <p className="references">
                            [1] Bach, S., Binder, A., Montavon, G., Klauschen, F., Müller, K.-R., &
                            Samek, W. (2015). On Pixel-Wise Explanations for Non-Linear Classifier
                            Decisions by Layer-Wise Relevance Propagation. PLOS ONE, 10(7)
                        </p>
                        <p className="references">
                            [2] Lundberg, S., & Lee, S.-I. (2017). A Unified Approach to
                            Interpreting Model Predictions. Advances in Neural Information
                            Processing Systems
                        </p>
                        <p className="references">
                            [3] Ribeiro, M. T., Singh, S., & Guestrin, C. (2016). "Why Should I
                            Trust You?": Explaining the Predictions of Any Classifier. International
                            Conference on Knowledge Discovery and Data Mining
                        </p>
                        <p className="references">
                            [4] McInnes, L., Healy, J., & Melville, J. (2018). UMAP: Uniform
                            Manifold Approximation and Projection for Dimension Reduction. arXiv
                            preprint arXiv:1802.03426
                        </p>
                        <p className="references">
                            [5] Collaris, D., & Van Wijk, J. (2022). StrategyAtlas: Strategy
                            Analysis for Machine Learning Interpretability. IEEE Transactions on
                            Visualization and Computer Graphics
                        </p>
                        <p className="references">
                            [6] Delaney, E., Greene, D., & Keane, M. T. (2021). Instance-Based
                            Counterfactual Explanations for Time Series Classification.
                            International Conference on Case-Based Reasoning, 32-47
                        </p>
                        <p className="references">
                            [7] Dau, H. A., Keogh, E., Kamgar, K., et al. (2018). The UCR Time
                            Series Classification Archive
                        </p>
                        <p className="references">
                            See also our related work{' '}
                            <Link to="/xai-tsc-survey">
                                Explainable AI for Time Series Classification: A Review, Taxonomy
                                and Research Directions
                            </Link>
                            .
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
            </Container>
        </section>
    );
}
